import { useQuery } from '@tanstack/react-query';
import {
  transformSkills,
  sortByDateDesc,
  type Award,
  type Certification,
  type Experience,
  type Project,
} from '@personal-portfolio/contract';
import { portfolioKeys } from './keys.js';
import { usePortfolioClient } from './client-context.js';

// Every queryFn in this file forwards TanStack Query's AbortSignal to the
// client, so unmounts and query cancellation abort the underlying fetch rather
// than orphaning it.

// Module-scope selects keep a stable identity so results stay memoized.
// Each sorts newest-first; entries with a missing or unparseable date sort
// last, so an unfinished CMS entry never jumps to the top of a page.
const selectAwards = (awards: Award[]) =>
  sortByDateDesc(awards, (award) => award.date);
const selectCertifications = (certifications: Certification[]) =>
  sortByDateDesc(certifications, (certification) => certification.issueDate);
const selectProjects = (projects: Project[]) =>
  sortByDateDesc(projects, (project) => project.startDate);
const selectExperience = (experience: Experience[]) =>
  sortByDateDesc(experience, (exp) => exp.startDate);

export const useProfile = () => {
  const client = usePortfolioClient();
  return useQuery({
    queryKey: portfolioKeys.profile(),
    queryFn: ({ signal }) => client.getProfile({ signal }),
  });
};

/**
 * The raw copy query, for the app's gate to drive its splash and retry UI.
 * Components read copy through `useSiteContent()` instead, which is
 * synchronous because the gate only renders them once this has resolved.
 *
 * staleTime 0, unlike every other query here: a cached copy renders
 * immediately (no splash for a returning visitor) while a background refetch
 * replaces it the moment the CMS changes.
 */
export const useSiteContentQuery = () => {
  const client = usePortfolioClient();
  return useQuery({
    queryKey: portfolioKeys.siteContent(),
    queryFn: ({ signal }) => client.getSiteContent({ signal }),
    staleTime: 0,
  });
};
/**
 * Note the reshape: `data` is NOT `Skill[]` but a
 * `Record<lowercased category name, Skill[]>`. A skill with several categories
 * appears under each of them, so the grouped values are not a partition of the
 * original list and counting across groups double-counts.
 */
export const useSkills = () => {
  const client = usePortfolioClient();
  return useQuery({
    queryKey: portfolioKeys.skills(),
    queryFn: ({ signal }) => client.getSkills({ signal }),
    select: transformSkills,
  });
};

export const useEducation = () => {
  const client = usePortfolioClient();
  return useQuery({
    queryKey: portfolioKeys.education(),
    queryFn: ({ signal }) => client.getEducation({ signal }),
  });
};

export const useAwards = () => {
  const client = usePortfolioClient();
  return useQuery({
    queryKey: portfolioKeys.awards(),
    queryFn: ({ signal }) => client.getAwards({ signal }),
    select: selectAwards,
  });
};

export const useCertifications = () => {
  const client = usePortfolioClient();
  return useQuery({
    queryKey: portfolioKeys.certifications(),
    queryFn: ({ signal }) => client.getCertifications({ signal }),
    select: selectCertifications,
  });
};

export const useProjects = () => {
  const client = usePortfolioClient();
  return useQuery({
    queryKey: portfolioKeys.projects(),
    queryFn: ({ signal }) => client.getProjects({ signal }),
    select: selectProjects,
  });
};

export const useExperience = () => {
  const client = usePortfolioClient();
  return useQuery({
    queryKey: portfolioKeys.experience(),
    queryFn: ({ signal }) => client.getExperience({ signal }),
    select: selectExperience,
  });
};
